import { Form, Formik } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useInvoiceStore } from "@/features/invoices/store/invoice-store";
import type { Payment } from "@/features/invoices/types";
import { ApiError } from "@/lib/api";
import { centsToDollars, dollarsToCents, formatCents } from "@/lib/utils";

type RefundSource = "CHECK" | "CASH";

interface RefundFormValues {
  amountDollars: string;
  source: RefundSource;
  reference: string;
  reason: string;
}

interface RefundDialogProps {
  payment: Payment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RefundDialog({ payment, open, onOpenChange }: RefundDialogProps) {
  const refundPayment = useInvoiceStore((s) => s.refundPayment);
  const refundableCents = payment.refundableCents;

  const schema = Yup.object({
    amountDollars: Yup.string()
      .required("Refund amount is required.")
      .matches(/^\d+(\.\d{1,2})?$/, "Enter a valid dollar amount.")
      .test("positive", "Refund must be greater than 0.", (v) => parseFloat(v ?? "0") > 0)
      .test(
        "within-refundable",
        `Refund can't exceed ${formatCents(refundableCents)}.`,
        (v) => dollarsToCents(String(v ?? "0")) <= refundableCents,
      ),
    source: Yup.mixed<RefundSource>().oneOf(["CHECK", "CASH"]).required("Select a source."),
    reference: Yup.string().optional(),
    reason: Yup.string().max(250, "Reason is limited to 250 characters.").optional(),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Refund Payment</DialogTitle>
          <DialogDescription>
            Issue a refund against this payment. Up to {formatCents(refundableCents)} is refundable.
          </DialogDescription>
        </DialogHeader>
        <Formik<RefundFormValues>
          initialValues={{
            amountDollars: refundableCents > 0 ? centsToDollars(refundableCents) : "",
            source: "CHECK",
            reference: "",
            reason: "",
          }}
          validationSchema={schema}
          onSubmit={async (values) => {
            try {
              await refundPayment(payment.id, {
                amountCents: dollarsToCents(values.amountDollars),
                source: values.source,
                reference: values.reference || undefined,
                reason: values.reason || undefined,
              });
              toast.success("Refund issued.");
              onOpenChange(false);
            } catch (err) {
              toast.error(err instanceof ApiError ? err.message : "Couldn't issue the refund.");
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, isSubmitting }) => (
            <Form className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="amountDollars">Amount</Label>
                  <Input
                    id="amountDollars"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={values.amountDollars}
                    onChange={(e) => setFieldValue("amountDollars", e.target.value)}
                  />
                  {touched.amountDollars && errors.amountDollars && (
                    <p className="text-destructive text-sm">{errors.amountDollars}</p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="refundSource">Source</Label>
                  <Select value={values.source} onValueChange={(v) => setFieldValue("source", v)}>
                    <SelectTrigger id="refundSource" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CHECK">Check</SelectItem>
                      <SelectItem value="CASH">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="refundReference">Reference</Label>
                <Input
                  id="refundReference"
                  placeholder="Check #, etc."
                  value={values.reference}
                  onChange={(e) => setFieldValue("reference", e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="refundReason">Reason</Label>
                <Textarea
                  id="refundReason"
                  rows={2}
                  value={values.reason}
                  onChange={(e) => setFieldValue("reason", e.target.value)}
                />
                {touched.reason && errors.reason && <p className="text-destructive text-sm">{errors.reason}</p>}
              </div>
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting || refundableCents <= 0}>
                  {isSubmitting ? "Issuing…" : "Issue Refund"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
